import React, { useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";

// Public Canny app ID provided by you
const CANNY_APP_ID = "68997f7217ebba13e1112cb9";

declare global {
  interface Window {
    Canny?: any;
  }
}

const loadCanny = (): Promise<void> => {
  return new Promise((resolve) => {
    // If already loaded, resolve immediately
    if (typeof window !== "undefined" && typeof window.Canny === "function") {
      resolve();
      return;
    }

    // If script tag exists, wait a bit for Canny to initialize
    const existing = document.getElementById("canny-jssdk");
    if (existing) {
      const check = () => {
        if (typeof window.Canny === "function") resolve();
        else setTimeout(check, 50);
      };
      check();
      return;
    }

    // Inject script (from official snippet)
    (function (w, d, i, s) {
      function l() {
        if (!d.getElementById(i)) {
          const f = d.getElementsByTagName(s)[0] as HTMLElement;
          const e = d.createElement(s);
          (e as HTMLScriptElement).type = "text/javascript";
          (e as HTMLScriptElement).async = true;
          (e as HTMLScriptElement).src = "https://canny.io/sdk.js";
          e.id = i;
          f.parentNode?.insertBefore(e, f);
        }
      }
      if (typeof w.Canny !== "function") {
        const c: any = function () {
          c.q.push(arguments);
        };
        c.q = [];
        (w as any).Canny = c;
        if (d.readyState === "complete") l();
        else if ((w as any).attachEvent) (w as any).attachEvent("onload", l);
        else w.addEventListener("load", l, false);
      }
    })(window, document, "canny-jssdk", "script");

    // Wait until window.Canny becomes available
    const check = () => {
      if (typeof window.Canny === "function") resolve();
      else setTimeout(check, 50);
    };
    check();
  });
};

const CannyIntegration: React.FC = () => {
  const { user } = useAuth();

  useEffect(() => {
    let isMounted = true;

    loadCanny().then(() => {
      if (!isMounted) return;

      // Initialize the floating Changelog widget (bottom-right)
      try {
        window.Canny("initChangelog", {
          appID: CANNY_APP_ID,
          position: "bottom",
          align: "right",
        });
      } catch (e) {
        // no-op
      }

      if (user) {
        // Map Supabase user to Canny identify payload
        const name =
          (user.user_metadata?.full_name as string | undefined) ||
          (user.user_metadata?.display_name as string | undefined) ||
          user.email ||
          "User";
        const avatarURL = (user.user_metadata?.avatar_url as string | undefined) || undefined;
        const createdISO = (user.created_at ? new Date(user.created_at).toISOString() : undefined);

        try {
          window.Canny("identify", {
            appID: CANNY_APP_ID,
            user: {
              id: user.id,
              email: user.email,
              name,
              avatarURL,
              created: createdISO,
            },
          });
        } catch (e) {
          // no-op
        }
      }
    });

    return () => {
      isMounted = false;
    };
  }, [user]);

  return null; // No UI; just loads SDK and identifies user
};

export default CannyIntegration;
